import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect } from 'react';
import Header from './../Common/Header';
import Footer from './../Common/Footer';
import Banner from './../Elements/Banner';
import { getProjects, resolveImageUrl as apiResolveImageUrl } from '../../services/api';
import { projectsData } from '../../data/siteData';
import '../../styles/pages/Projects.css';

var bnrimg = require('./../../images/banner/6.jpg');

const defaultProjects = projectsData;

// Image resolver consistent with Elements/Projects.jsx
const resolveImageUrl = (img) => {
    if (!img) return defaultProjects[0]?.image || '';
    if (typeof img === 'string') {
        if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) return img;
        return apiResolveImageUrl(img);
    }
    return img;
};

const galleryCategories = ["All", "ACP Front Elevation", "Facade Glazing", "Toughened Glass"];

const sanitizeCategory = (cat = '') => {
    if (!cat) return 'ACP Front Elevation';
    const c = cat.toLowerCase();
    if (c.includes('acp') || c.includes('elevation')) return 'ACP Front Elevation';
    if (c.includes('facade') || c.includes('glazing')) return 'Facade Glazing';
    if (c.includes('toughened') || c.includes('glass')) return 'Toughened Glass';
    return cat.trim();
};


const Projects = () => {
    const [activeFilter, setActiveFilter] = useState('All');
    const [selectedProject, setSelectedProject] = useState(null);
    const [projects, setProjects] = useState(defaultProjects);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const apiProjects = await getProjects({}, defaultProjects);
                if (apiProjects && Array.isArray(apiProjects) && apiProjects.length > 0) {
                    setProjects(apiProjects);
                }
            } catch (e) {
                console.warn('[Projects Page] API fetch fallback to default projects.');
            }
        };
        fetchProjects();

        const handleKey = (e) => { if (e.key === 'Escape') setSelectedProject(null); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, []);

    const cleanedProjects = projects.map(p => ({
        ...p,
        category: sanitizeCategory(p.category)
    }));

    // Full Gallery Page shows ALL projects matching filter
    const filteredProjects = activeFilter === 'All'
        ? cleanedProjects
        : cleanedProjects.filter(p => sanitizeCategory(p.category) === activeFilter);

    return (
        <>
            <Helmet>
                <title>bs clads</title>
                <meta name="description" content="Explore our complete portfolio of premier ACP Front Elevation, Facade Glazing, and Toughened Glass installations." />
            </Helmet>
            <Header />
            <div className="page-content">
                <Banner
                    title="BS CLADS Projects Gallery"
                    pagename="Projects"
                    description="Explore our complete portfolio of premier ACP Front Elevation, Facade Glazing, and Toughened Glass installations."
                    bgimage={bnrimg}
                />

                {/* PROJECTS GALLERY SECTION */}
                <div className="section-full p-tb80 bg-white">
                    <div className="container">
                        <div className="section-head text-center m-b40">
                            <div className="sx-separator-outer separator-center">
                                <div className="sx-separator bg-white">
                                    <h2 className="sep-line-one text-center" style={{ fontSize: 'clamp(24px, 5vw, 36px)', fontWeight: '800' }}>
                                         Projects Gallery
                                    </h2>
                                </div>
                            </div>
                        </div>

                        {/* CENTERED CATEGORY FILTER TABS */}
                        <div className="filter-navigation text-center" style={{ marginBottom: '45px', display: 'flex', justifyContent: 'center' }}>
                            <div className="filter-pills-container">
                                {galleryCategories.map((cat, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveFilter(cat)}
                                        className={`filter-btn ${activeFilter === cat ? 'active' : ''}`}
                                        style={{
                                            border: activeFilter === cat ? 'none' : '1px solid #ddd',
                                            backgroundColor: activeFilter === cat ? '#f03a02' : '#f8f9fa',
                                            color: activeFilter === cat ? '#ffffff' : '#333333',
                                            boxShadow: activeFilter === cat ? '0 4px 14px rgba(240, 58, 2, 0.35)' : 'none'
                                        }}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* FULL PROJECTS GRID - RESPONSIVE 1 COL ON MOBILE, 2 ON TAB, 3 ON DESKTOP */}
                        <div className="projects-grid-container">
                            {filteredProjects.map((project, index) => {
                                const imgSrc = resolveImageUrl(project.mainImage || project.image);
                                return (
                                    <div className="project-card-item" key={project.id || index}>
                                        <div
                                            className="project-grid-card"
                                            onClick={() => setSelectedProject({ ...project, imgSrc })}
                                            style={{
                                                backgroundColor: '#ffffff',
                                                borderRadius: '16px',
                                                overflow: 'hidden',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.06)',
                                                border: '1px solid #eeeeee',
                                                transition: 'transform 0.35s ease, box-shadow 0.35s ease',
                                                cursor: 'pointer',
                                                height: '270px',
                                                position: 'relative'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-6px)';
                                                e.currentTarget.style.boxShadow = '0 14px 30px rgba(0,0,0,0.14)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.06)';
                                            }}
                                        >
                                            <img
                                                src={imgSrc}
                                                alt="Project"
                                                onError={(e) => { e.target.onerror = null; e.target.src = defaultProjects[0]?.image; }}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* LIGHTBOX MODAL PREVIEW - CLEAN IMAGE ONLY */}
            {selectedProject && (
                <div className="project-modal-backdrop" onClick={() => setSelectedProject(null)} style={{
                    position: 'fixed',
                    top: 0, left: 0,
                    width: '100vw', height: '100vh',
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    zIndex: 9999999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div className="project-modal-content" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        maxWidth: '750px',
                        width: '100%',
                        overflow: 'hidden',
                        position: 'relative',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                        textAlign: 'center'
                    }}>
                        <button
                            onClick={() => setSelectedProject(null)}
                            style={{
                                position: 'absolute', top: '15px', right: '15px',
                                background: '#111111', color: '#ffffff',
                                border: 'none', borderRadius: '50%',
                                width: '36px', height: '36px',
                                fontSize: '18px', cursor: 'pointer', zIndex: 10
                            }}
                        >
                            &times;
                        </button>
                        <img
                            src={selectedProject.imgSrc}
                            alt="Project Preview"
                            onError={(e) => { e.target.onerror = null; e.target.src = defaultProjects[0]?.image; }}
                            style={{ width: '100%', maxHeight: '550px', objectFit: 'contain', backgroundColor: '#111' }}
                        />
                    </div>
                </div>
            )}

            <Footer />
        </>
    );
};

export default Projects;
